/**
 * The three rounds made easier (Marek, 6 Oct 2026, after the first real evening: "hard level of the
 * games are too hard"). Every board's level table changed the same way: the old round 2 is the new
 * round 3, the new round 2 sits halfway between the old rounds 1 and 2, and round 1 stays as it was.
 * A table keeps its old numbers in the source, wrapped in `easier`, so the change can be read.
 */

/**
 * Halfway between two settings of a level, the easier one first. Numbers meet in the middle; a
 * count that would fall on a half rounds towards the easier side. Lists go item by item when they
 * are the same length. Anything else (a switch, a word, an endless time, lists of other lengths)
 * keeps the easier setting.
 */
export function halfway<T>(easy: T, hard: T): T {
  if (typeof easy === 'number' && typeof hard === 'number') {
    if (!Number.isFinite(easy) || !Number.isFinite(hard)) return easy;
    const mid = (easy + hard) / 2;
    if (Number.isInteger(easy) && Number.isInteger(hard) && !Number.isInteger(mid)) {
      return (easy < hard ? Math.floor(mid) : Math.ceil(mid)) as T;
    }
    return (Math.round(mid * 10_000) / 10_000) as T;
  }
  if (Array.isArray(easy) && Array.isArray(hard)) {
    if (easy.length !== hard.length) return easy;
    const to = hard as unknown[];
    return (easy as unknown[]).map((v, i) => halfway(v, to[i])) as T;
  }
  if (easy && hard && typeof easy === 'object' && typeof hard === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(easy)) out[k] = k in hard ? halfway(v, (hard as Record<string, unknown>)[k]) : v;
    return out as T;
  }
  return easy;
}

/**
 * A board's three rounds, from the table it had before 6 Oct 2026: round 1 as it was, round 2
 * halfway between the old rounds 1 and 2, round 3 the old round 2. The old round 3 is left out.
 * `middle` overrides parts of the new round 2 where halfway is not a sensible setting.
 */
export function easier<T>(old: readonly T[], middle: Partial<T> = {}): readonly [T, T, T] {
  const a = old[0]!;
  const b = old[1] ?? a;
  return [a, { ...halfway(a, b), ...middle }, b];
}

/** The same for a plain list of numbers, one a round. */
export function easierNumbers(old: readonly number[]): readonly [number, number, number] {
  const a = old[0]!;
  const b = old[1] ?? a;
  return [a, halfway(a, b), b];
}
